<template>
  <component v-if="!isDefaultTemplate"
    :is="templateComponent"
    class="common-template"
    v-bind="templateProps"
    v-on="$listeners"
  />
  <td v-else-if="renderCell && typeof value === 'boolean'" class="common-template" :class="[{ editable: isEditable }, className]">
    <base-icon-clickable v-if="isExpandColumn" class="m-auto" :name="isExpanded ? 'chevronup' : 'chevrondown'" size="sm" @click.stop="$emit('expandchange', { dataItem, event: $event, field, value: !expanded })" />
    <input v-else :class="{ 'k-radio': single, 'k-checkbox': !single }" :type="single ? 'radio' : 'checkbox'" :name="finalTableId" :checked="dataItem[field]" @change="$emit('selectionchange', { event: $event, dataItem })" @click.stop="selectClick" />
  </td>
  <td v-else-if="renderCell" class="common-template" :class="[{ editable: isEditable }, className]" @click="$emit('cellclick', { dataItem, field, event: $event })">
    <template v-if="isEditable">
      <el-input ref="cellinput" class="w-full" v-model="localValue" size="mini" tabindex="0" @change="$emit('change', null, $event)" />
    </template>
    <template v-else>
      {{ value }}
    </template>
  </td>
  <td v-else-if="renderArrow" :key="`g-${columnIndex}`" class="k-group-cell" :class="className" />
  <td v-else-if="renderGroup" key="g-colspan" class="common-template" :colSpan="columnsCount - columnIndex" :class="className">
    <p class="k-reset">
      <a @click="$emit('click', $event, dataItem, expanded)" tabIndex="-1" href="#" class="k-icon" :class="expanded ? 'k-i-collapse' : 'k-i-expand'" />
      {{ groupHeader }}
    </p>
  </td>
</template>
<script>
import camelCase from 'lodash/camelCase'
import templates from '@/components/kendo/templates/_requireTemplates'
import getNestedValue from '@/services/functions/getNestedValue'
import { discard } from '@/services/functions/preserve&discard'
import NULL from '@/constants/NULL'

export default {
  name: 'common-template',
  props: {
    tableId: [String, Number],
    field: String,
    dataItem: Object,
    format: String,
    className: String,
    columnIndex: Number,
    columnsCount: Number,
    rowType: String,
    level: Number,
    selectedField: String,
    expandField: String,
    expanded: Boolean,
    editor: String,
    type: {
      type: String,
      default: 'Text'
    },
    curEditField: [String, null],
    editType: {
      type: [String, Boolean],
      default: 'row'
    },
    editField: {
      type: String,
      default: 'inEdit'
    },
    extraparams: {
      type: Object,
      default: () => ({})
    },
    cell: {
      type: String,
      default: 'default',
      validator: function (value) {
        const finalValue = camelCase(value)
        return finalValue === 'default' || Object.prototype.hasOwnProperty.call(templates, finalValue)
      }
    },
    single: {
      type: Boolean,
      default: false
    },
    selectOnRowClick: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      localValue: ''
    }
  },
  mounted () {
    this.localValue = this.value
    if (this.isEditable && this.curEditField === this.field && this.$refs.cellinput) {
      this.$refs.cellinput.focus()
    }
  },
  watch: {
    value: function (value, oldValue) {
      if (value !== oldValue) {
        this.localValue = value
      }
    },
    isEditable: function (value, oldValue) {
      if (value && value !== oldValue) {
        this.$nextTick(() => {
          if (this.isEditable && this.curEditField === this.field && this.$refs.cellinput) {
            this.$refs.cellinput.focus()
          }
        })
      }
    }
  },
  computed: {
    finalTableId: function () {
      const prefix = this.single ? 'k-radio' : 'k-checkbox'
      return `${prefix}-${this.field}-${this.tableId}`
    },
    renderArrow () {
      var returnValue =
        this.columnIndex === undefined ||
        this.level === undefined ||
        this.columnIndex < this.level ||
        this.columnsCount === undefined ||
        this.rowType !== 'groupHeader' ||
        this.dataItem[this.field] === undefined
      // return returnValue && this.dataItem[this.field]
      return returnValue && this.dataItem[this.field] !== undefined
    },
    renderCell () {
      return this.field !== undefined && this.rowType !== 'groupHeader'
    },
    renderGroup () {
      return this.columnIndex <= this.level
    },
    inEdit: function () {
      return getNestedValue(this.editField, this.dataItem) === true
    },
    value: function () {
      const value = getNestedValue(this.field, this.dataItem)
      return value === NULL ? null : value
    },
    groupHeader () {
      const value = this.value
      if (this.rowType === 'groupHeader') {
        const template = this.extraparams.groupHeaderTemplate
        if (typeof template === 'function') {
          return template(this.dataItem, { value, ...discard([template.name], this.extraparams) })
        }
      }
      if (value === null || value === undefined) return '' 
      return value 
    },
    camelCell: function () {
      return camelCase(this.cell)
    },
    isDefaultTemplate: function () {
      return ['default', 'common'].includes(this.camelCell)
    },
    templateComponent: function () {
      if (this.isDefaultTemplate) {
        return null
      }
      return templates[this.camelCell]
    },
    templateProps: function () {
      if (this.isDefaultTemplate) {
        return {}
      }
      return this.discard(['cell'], this.$props)
    },
    editable: function () {
      return !!this.extraparams.editable
    },
    isCellEditable: function () {
      return this.editType === 'cell'
    },
    isEditable: function () {
      return this.editable && this.inEdit && (!this.isCellEditable || this.curEditField === this.field)
    },
    isExpanded: function () {
      return getNestedValue(this.expandField, this.dataItem)
    },
    isExpandColumn: function () {
      return this.expandField === this.field
    },
    isSelected: function () {
      return getNestedValue(this.selectedField, this.dataItem)
    },
    isSelectColumn: function () {
      return this.selectedField === this.field
    }
  },
  methods: {
    selectClick: function (event) {
      if (event.target.type === 'radio' && this.isSelectColumn && this.isSelected) {
        this.$emit('selectionchange', { event: event, dataItem: this.dataItem })
      }
    }
  },
}
</script>

<style scoped>
.bg-success-p{
  background-color: black;
}
.k-grid td.editable {
  text-overflow: clip;
}
.k-grid td.editable::v-deep .el-input__inner {
  min-width: calc(30px + 1em);
}
</style>
